import {
  TextField,
  Grid2 as Grid,
  Stack,
  Alert,
  Link,
  Button
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { CartState } from '../../../contexts/cart/types';
import { PhoneField } from './PhoneField';
import { Link as RouterLink } from 'react-router-dom';
import { UXB_ROUTE_NAMES } from 'supplier-portals/uxbeauty/UXBRouteNames';
import { SHIPPING_SECTION_ID } from 'components/pages/user/UserProfile/UserProfile';

export const ShippingDetailsForm: React.FC<{
  profileIsIncomplete?: boolean;
}> = ({ profileIsIncomplete }) => {
  const {
    register,
    formState: { errors }
  } = useFormContext<CartState>();

  return (
    <Stack spacing={3}>
      {profileIsIncomplete ? (
        <ProfileIncompleteAlert />
      ) : (
        <SwitchBackToProfileDataAlert />
      )}
      <Grid container spacing={2}>
        <Grid size={{ sm: 6, md: 4 }}>
          <TextField
            fullWidth
            label="Name"
            required
            {...register('shippingDetails.name', {
              required: 'Name is required.',
              validate: value => value.trim() !== '' || 'Name cannot be empty.'
            })}
            error={!!errors.shippingDetails?.name}
            helperText={errors.shippingDetails?.name?.message}
          />
        </Grid>
        <Grid size={{ sm: 6, md: 4 }}>
          <PhoneField name="shippingDetails.phoneNumber" />
        </Grid>
        <Grid size={{ sm: 6, md: 4 }}>
          <TextField
            fullWidth
            label="Email"
            required
            {...register('shippingDetails.email', {
              required: 'Email is required.',
              validate: value => value.trim() !== '' || 'Email cannot be empty.'
            })}
            error={!!errors.shippingDetails?.email}
            helperText={errors.shippingDetails?.email?.message}
          />
        </Grid>
        <Grid size={{ sm: 6, md: 4 }}>
          <TextField
            fullWidth
            label="Company"
            required
            {...register('shippingDetails.company', {
              required: 'Company is required.',
              validate: value =>
                value.trim() !== '' || 'Company cannot be empty.'
            })}
            error={!!errors.shippingDetails?.company}
            helperText={errors.shippingDetails?.company?.message}
          />
        </Grid>
        <Grid size={{ sm: 6, md: 4 }}>
          <TextField
            fullWidth
            label="Street"
            required
            {...register('shippingDetails.street', {
              required: 'Street is required.',
              validate: value =>
                value.trim() !== '' || 'Street cannot be empty.'
            })}
            error={!!errors.shippingDetails?.street}
            helperText={errors.shippingDetails?.street?.message}
          />
        </Grid>
        <Grid size={{ sm: 6, md: 4 }}>
          <TextField
            fullWidth
            label="Street 2"
            {...register('shippingDetails.street2')}
          />
        </Grid>
        <Grid size={{ sm: 6, md: 4 }}>
          <TextField
            fullWidth
            label="Post code"
            required
            {...register('shippingDetails.postCode', {
              required: 'Post code is required.',
              validate: value =>
                value.trim() !== '' || 'Post code cannot be empty.'
            })}
            error={!!errors.shippingDetails?.postCode}
            helperText={errors.shippingDetails?.postCode?.message}
          />
        </Grid>
        <Grid size={{ sm: 6, md: 4 }}>
          <TextField
            fullWidth
            label="City"
            required
            {...register('shippingDetails.city', {
              required: 'City is required.',
              validate: value => value.trim() !== '' || 'City cannot be empty.'
            })}
            error={!!errors.shippingDetails?.city}
            helperText={errors.shippingDetails?.city?.message}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Country"
            required
            {...register('shippingDetails.country', {
              required: 'Country is required.',
              validate: value =>
                value.trim() !== '' || 'Country cannot be empty.'
            })}
            error={!!errors.shippingDetails?.country}
            helperText={errors.shippingDetails?.country?.message}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

const ProfileIncompleteAlert: React.FC = () => {
  return (
    <Alert severity="warning" icon={false}>
      To avoid entering your address manually with each request, you can save
      your shipping details in your{' '}
      <Link
        component={RouterLink}
        to={`${UXB_ROUTE_NAMES.userProfile}#${SHIPPING_SECTION_ID}`}
      >
        user profile
      </Link>
      . Ensure your saved address is complete.
    </Alert>
  );
};

const SwitchBackToProfileDataAlert: React.FC = () => {
  const { setValue } = useFormContext<CartState>();
  return (
    <Alert
      severity="info"
      icon={false}
      action={
        <Button
          color="inherit"
          size="small"
          variant="outlined"
          onClick={() => setValue('hasCustomShippingDetails', false)}
        >
          Use default address
        </Button>
      }
    >
      You decided to use a custom shipping address for this request. You can
      still{' '}
      <Link
        component={RouterLink}
        to={`${UXB_ROUTE_NAMES.userProfile}#${SHIPPING_SECTION_ID}`}
      >
        go to your profile page
      </Link>{' '}
      to edit your default shipping address.
    </Alert>
  );
};
